import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    IconButton,
    CircularProgress,
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon, Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import Input from '../../../components/Input';

function AddDeviceTypeModal({ open, onClose, onConfirm, deviceTypeData, editMode = false }) {
    const { t } = useTranslation();

    const [formData, setFormData] = useState({
        name: '',
        description: '',
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Reset form when modal opens/closes or when deviceTypeData changes
    useEffect(() => {
        if (open) {
            if (editMode && deviceTypeData) {
                setFormData({
                    name: deviceTypeData.name || '',
                    description: deviceTypeData.description || '',
                });
            } else {
                setFormData({
                    name: '',
                    description: '',
                });
            }
            setErrors({});
            setIsSubmitting(false);
        }
    }, [open, editMode, deviceTypeData]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = t('deviceConfig.deviceTypes.validation.nameRequired');
        } else if (formData.name.length > 100) {
            newErrors.name = t('deviceConfig.deviceTypes.validation.nameMaxLength');
        }

        if (formData.description && formData.description.length > 255) {
            newErrors.description = t('deviceConfig.deviceTypes.validation.descriptionMaxLength');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            const submitData = { ...formData };

            if (editMode && deviceTypeData) {
                submitData.id = deviceTypeData.id;
            }

            await onConfirm(submitData);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid #e0e0e0',
                    pb: 2,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {editMode ? <EditIcon color="primary" /> : <AddIcon color="primary" />}
                    {editMode ? t('deviceConfig.deviceTypes.editDialog.title') : t('deviceConfig.deviceTypes.addDialog.title')}
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 4 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Input
                        label={t('deviceConfig.deviceTypes.form.name')}
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        error={!!errors.name}
                        helperText={errors.name}
                        required
                        fullWidth
                        id="name"
                        data-testid="deviceType-modal-input-name"
                        sx={{ mt: 3 }}
                    />

                    <Input
                        label={t('deviceConfig.deviceTypes.form.description')}
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        error={!!errors.description}
                        helperText={errors.description}
                        fullWidth
                        multiline
                        rows={3}
                        id="description"
                        data-testid="deviceType-modal-input-description"
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    disabled={isSubmitting}
                    id="cancel"
                    name="cancel"
                    data-testid="deviceType-modal-button-cancel"
                >
                    {t('deviceConfig.deviceTypes.form.cancel')}
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
                    id="confirm"
                    name="confirm"
                    data-testid="deviceType-modal-button-confirm"
                >
                    {editMode ? t('deviceConfig.deviceTypes.form.update') : t('deviceConfig.deviceTypes.form.create')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default AddDeviceTypeModal;
